import {
  CalendarOutlined,
  CheckSquareOutlined,
  CodeSandboxOutlined,
  FontSizeOutlined,
  NumberOutlined,
  QuestionCircleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { Button, List, Popover } from 'antd'
import { OntologyFieldType } from 'features/dynamicOntology'
import { FC, useCallback, useMemo, useState } from 'react'
import CantileverTheme from 'styles/cantileverTheme'

export type PropertyType = OntologyFieldType

interface PropertyTypeSelectProps {
  selectedType?: PropertyType
  setSelectedType: (type: PropertyType) => void
  allowedTypes?: ReadonlyArray<PropertyType>
  overrideButtonIcon?: React.ReactNode
}

const PROPERTY_TYPE_ICONS: Record<PropertyType, React.ReactNode> = {
  text: <FontSizeOutlined />,
  number: <NumberOutlined />,
  boolean: <CheckSquareOutlined />,
  enum: <UnorderedListOutlined />,
  date: <CalendarOutlined />,
  object: <CodeSandboxOutlined />,
  array: <UnorderedListOutlined />,
}

const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'Boolean',
  enum: 'Enum',
  date: 'Date',
  object: 'Object',
  array: 'Array',
}

const DEFAULT_ALLOWED_TYPES: ReadonlyArray<PropertyType> = [
  'text',
  'number',
  'boolean',
  'enum',
  'date',
]

/**
 * A button which, on hover, displays a list of property types to select from.
 *
 * By default, the button shows the icon of the currently selected type, of '?' if no type is selected.
 * It can be overridden with an icon prop.
 */
export const PropertyTypeSelect: FC<PropertyTypeSelectProps> = ({
  selectedType,
  setSelectedType: onSelect,
  allowedTypes,
  overrideButtonIcon,
}) => {
  const [hoveredType, setHoveredType] = useState<PropertyType | null>(null)

  const handleSelect = useCallback(
    (value: PropertyType) => {
      onSelect(value)
    },
    [onSelect]
  )

  const selectOptions = useMemo(() => {
    const effectiveAllowedTypes = allowedTypes ?? DEFAULT_ALLOWED_TYPES
    return effectiveAllowedTypes.map((type) => ({
      value: type,
      label: PROPERTY_TYPE_LABELS[type],
      icon: PROPERTY_TYPE_ICONS[type],
    }))
  }, [allowedTypes])

  const popoverContent = useMemo(
    () => (
      <List
        dataSource={selectOptions}
        rowKey='value'
        size='small'
        split={false}
        renderItem={({ value, label, icon }) => (
          <List.Item
            onClick={() => handleSelect(value)}
            onMouseEnter={() => setHoveredType(value)}
            onMouseLeave={() => setHoveredType(null)}
            style={{
              cursor: 'pointer',
              backgroundColor:
                value === selectedType
                  ? CantileverTheme.colors.grayscale['400']
                  : value === hoveredType
                    ? CantileverTheme.colors.grayscale['600']
                    : undefined,
              padding: '8px 12px',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {icon}
              {label}
            </div>
          </List.Item>
        )}
      />
    ),
    [handleSelect, hoveredType, selectOptions, selectedType]
  )

  return (
    <Popover content={popoverContent} placement='top' trigger='hover'>
      <Button style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {overrideButtonIcon ??
          (selectedType ? (
            PROPERTY_TYPE_ICONS[selectedType]
          ) : (
            <QuestionCircleOutlined />
          ))}
      </Button>
    </Popover>
  )
}

export function inferPropertyType(value: unknown): PropertyType {
  if (typeof value === 'number') {
    return 'number'
  }
  if (typeof value === 'boolean') {
    return 'boolean'
  }

  // Check if the value is a valid date string
  // FLUP - use a library for this
  if (typeof value === 'string') {
    const parsedDate = new Date(value)
    if (!isNaN(parsedDate.getTime()) && value.trim() !== '') {
      // Additional check to ensure it's likely a date format (not just a number string)
      // This helps avoid interpreting plain numbers as dates
      if (value.includes('-') || value.includes('/') || value.includes('T')) {
        return 'date'
      }
    }
  }
  return 'text'
}
